/**
 * Exams & Report Card Page - Full Featured
 * Tabs: Exams, Marks Entry, Report Cards, Subjects
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }
};

const EXAM_TYPES = [
  { value: 'unit_test', label: 'Unit Test', labelHi: 'इकाई परीक्षा' },
  { value: 'half_yearly', label: 'Half Yearly', labelHi: 'अर्धवार्षिक' },
  { value: 'annual', label: 'Annual', labelHi: 'वार्षिक' },
  { value: 'weekly_test', label: 'Weekly Test', labelHi: 'साप्ताहिक' },
  { value: 'monthly_test', label: 'Monthly Test', labelHi: 'मासिक' },
];

// ==================== EXAMS LIST TAB ====================
function ExamsListTab({ token, language, classes, onSelectExam }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({
    name: '', exam_type: 'unit_test', class_id: '', section_id: '',
    subject_ids: [], start_date: '', end_date: '', max_marks: 100, passing_marks: 33
  });
  const [creating, setCreating] = useState(false);

  const loadExams = useCallback(async () => {
    const res = await api.get('/api/exams/', token);
    setExams(res.exams || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { loadExams(); }, [loadExams]);
  useEffect(() => {
    (async () => {
      const res = await api.get('/api/exams/subjects', token);
      setSubjects(res.subjects || []);
    })();
  }, [token]);

  const handleCreate = async () => {
    if (!form.name || !form.class_id || !form.start_date) return;
    setCreating(true);
    const res = await api.post('/api/exams/', form, token);
    setCreating(false);
    if (res.success) {
      setShowCreate(false);
      setForm({ name: '', exam_type: 'unit_test', class_id: '', section_id: '', subject_ids: [], start_date: '', end_date: '', max_marks: 100, passing_marks: 33 });
      loadExams();
    }
  };

  const toggleSubject = (sid) => {
    setForm(prev => ({
      ...prev,
      subject_ids: prev.subject_ids.includes(sid)
        ? prev.subject_ids.filter(id => id !== sid)
        : [...prev.subject_ids, sid]
    }));
  };

  const sections = classes.find(c => c.id === form.class_id)?.sections || [];

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="exams-list-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          {language === 'hi' ? `कुल ${exams.length} परीक्षाएं` : `${exams.length} exams total`}
        </p>
        <button data-testid="create-exam-btn" onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
          {showCreate ? '✕' : '+'} {language === 'hi' ? 'नई परीक्षा' : 'New Exam'}
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="create-exam-form">
          <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'नई परीक्षा बनाएं' : 'Create New Exam'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input data-testid="exam-name-input" placeholder={language === 'hi' ? 'परीक्षा का नाम *' : 'Exam Name *'}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" />
            <select data-testid="exam-type-select" value={form.exam_type} onChange={(e) => setForm({ ...form, exam_type: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm bg-white">
              {EXAM_TYPES.map(t => <option key={t.value} value={t.value}>{language === 'hi' ? t.labelHi : t.label}</option>)}
            </select>
            <select data-testid="exam-class-select" value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value, section_id: '' })}
              className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">{language === 'hi' ? 'कक्षा *' : 'Class *'}</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={form.section_id} onChange={(e) => setForm({ ...form, section_id: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm bg-white" disabled={!form.class_id}>
              <option value="">{language === 'hi' ? 'सेक्शन (वैकल्पिक)' : 'Section (optional)'}</option>
              {sections.map(s => <option key={s.id} value={s.id}>Section {s.name}</option>)}
            </select>
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" placeholder="Start Date" />
            <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" placeholder="End Date" />
            <input type="number" value={form.max_marks} onChange={(e) => setForm({ ...form, max_marks: parseInt(e.target.value) || 100 })}
              className="px-3 py-2 border rounded-lg text-sm" placeholder="Max Marks" />
            <input type="number" value={form.passing_marks} onChange={(e) => setForm({ ...form, passing_marks: parseInt(e.target.value) || 33 })}
              className="px-3 py-2 border rounded-lg text-sm" placeholder="Passing Marks" />
          </div>
          {/* Subject Selection */}
          {subjects.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">{language === 'hi' ? 'विषय चुनें' : 'Select Subjects'}</label>
              <div className="flex flex-wrap gap-2">
                {subjects.map(s => (
                  <button key={s.id} type="button" onClick={() => toggleSubject(s.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${form.subject_ids.includes(s.id)
                      ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button data-testid="save-exam-btn" onClick={handleCreate} disabled={creating || !form.name || !form.class_id}
            className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {creating ? '...' : (language === 'hi' ? 'परीक्षा बनाएं' : 'Create Exam')}
          </button>
        </div>
      )}

      {/* Exams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {exams.map(exam => {
          const typeInfo = EXAM_TYPES.find(t => t.value === exam.exam_type) || {};
          const classInfo = classes.find(c => c.id === exam.class_id);
          return (
            <div key={exam.id} className="bg-white rounded-xl border p-4 hover:shadow-md transition cursor-pointer group"
              onClick={() => onSelectExam(exam)} data-testid={`exam-card-${exam.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-gray-800 group-hover:text-orange-600 transition">{exam.name}</h4>
                  <span className="text-xs text-gray-500">{classInfo?.name || (language === 'hi' ? 'कक्षा' : 'Class')}</span>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                  exam.exam_type === 'annual' ? 'bg-purple-100 text-purple-700' :
                  exam.exam_type === 'half_yearly' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {language === 'hi' ? typeInfo.labelHi : typeInfo.label}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
                <span>{exam.start_date}</span>
                <span>→</span>
                <span>{exam.end_date || '-'}</span>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs">
                <span className="text-orange-600 font-medium">{language === 'hi' ? `अधिकतम: ${exam.max_marks}` : `Max: ${exam.max_marks}`}</span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-500">{language === 'hi' ? `पास: ${exam.passing_marks}` : `Pass: ${exam.passing_marks}`}</span>
              </div>
            </div>
          );
        })}
      </div>

      {exams.length === 0 && !showCreate && (
        <div className="text-center py-16 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">📝</div>
          <p className="text-lg">{language === 'hi' ? 'कोई परीक्षा नहीं बनाई गई' : 'No exams created yet'}</p>
          <button onClick={() => setShowCreate(true)} className="mt-3 text-orange-500 text-sm font-medium hover:underline">
            {language === 'hi' ? '+ पहली परीक्षा बनाएं' : '+ Create your first exam'}
          </button>
        </div>
      )}
    </div>
  );
}

// ==================== MARKS ENTRY TAB ====================
function MarksEntryTab({ token, language, classes, selectedExam }) {
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [exam, setExam] = useState(selectedExam?.id || '');
  const [subject, setSubject] = useState('');
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [existingResults, setExistingResults] = useState([]);

  useEffect(() => {
    (async () => {
      const [exRes, subRes] = await Promise.all([
        api.get('/api/exams/', token),
        api.get('/api/exams/subjects', token)
      ]);
      setExams(exRes.exams || []);
      setSubjects(subRes.subjects || []);
    })();
  }, [token]);

  useEffect(() => {
    if (selectedExam?.id) setExam(selectedExam.id);
  }, [selectedExam]);

  const selectedExamData = exams.find(e => e.id === exam);

  const loadStudents = useCallback(async () => {
    if (!exam || !selectedExamData) return;
    const classId = selectedExamData.class_id;
    const sectionId = selectedExamData.section_id;
    let url = `/api/students/?class_id=${classId}`;
    if (sectionId) url += `&section_id=${sectionId}`;
    const res = await api.get(url, token);
    setStudents(res.students || []);

    // Load existing results
    const resultsRes = await api.get(`/api/exams/results/${exam}`, token);
    setExistingResults(resultsRes.results || []);

    // Pre-fill marks if subject selected
    if (subject) {
      const marksMap = {};
      (resultsRes.results || []).filter(r => r.subject_id === subject).forEach(r => {
        marksMap[r.student_id] = r.marks_obtained;
      });
      setMarks(marksMap);
    }
  }, [exam, selectedExamData, token, subject]);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  // When subject changes, re-fill marks from existing results
  useEffect(() => {
    if (subject && existingResults.length > 0) {
      const marksMap = {};
      existingResults.filter(r => r.subject_id === subject).forEach(r => {
        marksMap[r.student_id] = r.marks_obtained;
      });
      setMarks(marksMap);
    } else {
      setMarks({});
    }
  }, [subject, existingResults]);

  const handleSave = async () => {
    if (!exam || !subject) return;
    setSaving(true);
    const entries = students.map(s => ({
      student_id: s.id,
      marks_obtained: parseFloat(marks[s.id]) || 0
    }));
    const res = await api.post('/api/exams/marks/bulk', { exam_id: exam, subject_id: subject, entries }, token);
    setSaving(false);
    if (res.success) {
      setToast({ message: language === 'hi' ? `${res.entered} छात्रों के अंक सेव हुए!` : `Marks saved for ${res.entered} students!`, type: 'success' });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const examSubjects = selectedExamData?.subject_ids?.length > 0
    ? subjects.filter(s => selectedExamData.subject_ids.includes(s.id))
    : subjects;

  return (
    <div className="space-y-4" data-testid="marks-entry-tab">
      {/* Filters */}
      <div className="bg-white rounded-xl border p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select data-testid="marks-exam-select" value={exam} onChange={(e) => { setExam(e.target.value); setSubject(''); setMarks({}); }}
            className="px-3 py-2 border rounded-lg text-sm bg-white">
            <option value="">{language === 'hi' ? 'परीक्षा चुनें' : 'Select Exam'}</option>
            {exams.map(e => <option key={e.id} value={e.id}>{e.name} ({classes.find(c => c.id === e.class_id)?.name || ''})</option>)}
          </select>
          <select data-testid="marks-subject-select" value={subject} onChange={(e) => setSubject(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white" disabled={!exam}>
            <option value="">{language === 'hi' ? 'विषय चुनें' : 'Select Subject'}</option>
            {examSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {selectedExamData && (
              <>
                <span className="px-2 py-1 bg-orange-50 text-orange-600 rounded text-xs font-medium">
                  {language === 'hi' ? `अधिकतम: ${selectedExamData.max_marks}` : `Max: ${selectedExamData.max_marks}`}
                </span>
                <span className="px-2 py-1 bg-gray-50 text-gray-600 rounded text-xs font-medium">
                  {language === 'hi' ? `पास: ${selectedExamData.passing_marks}` : `Pass: ${selectedExamData.passing_marks}`}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Marks Table */}
      {exam && subject && students.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="marks-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'छात्र' : 'Student'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'रोल नं.' : 'Adm No.'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? `अंक (/${selectedExamData?.max_marks || 100})` : `Marks (/${selectedExamData?.max_marks || 100})`}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'ग्रेड' : 'Grade'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {students.map((student, idx) => {
                  const m = parseFloat(marks[student.id]) || 0;
                  const maxM = selectedExamData?.max_marks || 100;
                  const passM = selectedExamData?.passing_marks || 33;
                  const grade = m >= maxM * 0.9 ? 'A+' : m >= maxM * 0.8 ? 'A' : m >= maxM * 0.7 ? 'B+' : m >= maxM * 0.6 ? 'B' : m >= maxM * 0.5 ? 'C' : m >= passM ? 'D' : 'F';
                  const isFail = m < passM && marks[student.id] !== undefined && marks[student.id] !== '';
                  return (
                    <tr key={student.id} className={`hover:bg-gray-50 ${isFail ? 'bg-red-50/50' : ''}`}>
                      <td className="px-4 py-2.5 text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{student.name}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-500">{student.admission_number || '-'}</td>
                      <td className="px-4 py-2.5 text-center">
                        <input
                          type="number" min="0" max={maxM}
                          value={marks[student.id] ?? ''}
                          onChange={(e) => setMarks(prev => ({ ...prev, [student.id]: e.target.value }))}
                          className="w-20 px-2 py-1.5 border rounded-lg text-sm text-center focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                          placeholder="-"
                          data-testid={`marks-${student.id}`}
                        />
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {marks[student.id] !== undefined && marks[student.id] !== '' && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            grade === 'F' ? 'bg-red-100 text-red-700' :
                            grade === 'A+' || grade === 'A' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>{grade}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t bg-gray-50">
            <button data-testid="save-marks-btn" onClick={handleSave} disabled={saving}
              className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
              {saving ? '...' : (language === 'hi' ? 'अंक सेव करें' : 'Save Marks')}
            </button>
          </div>
        </div>
      )}

      {exam && subject && students.length === 0 && (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-4xl mb-2">👨‍🎓</div>
          {language === 'hi' ? 'इस कक्षा में कोई छात्र नहीं' : 'No students found for this class'}
        </div>
      )}

      {(!exam || !subject) && (
        <div className="text-center py-16 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">✍️</div>
          <p>{language === 'hi' ? 'परीक्षा और विषय चुनें' : 'Select exam and subject to enter marks'}</p>
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white text-sm z-50 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ==================== REPORT CARD TAB ====================
function ReportCardTab({ token, language, classes }) {
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [classResults, setClassResults] = useState(null);
  const [reportCard, setReportCard] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [format, setFormat] = useState('cbse');

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/exams/', token);
      setExams(res.exams || []);
    })();
  }, [token]);

  const loadClassResults = async (examId) => {
    setSelectedExam(examId);
    setLoading(true);
    setReportCard(null);
    setSelectedStudent(null);
    const res = await api.get(`/api/exams/class-results/${examId}`, token);
    setClassResults(res);
    setLoading(false);
  };

  const loadReportCard = async (studentId) => {
    setSelectedStudent(studentId);
    setLoading(true);
    const res = await api.get(`/api/exams/report-card/${studentId}/${selectedExam}`, token);
    setReportCard(res);
    setLoading(false);
  };

  const FORMATS = [
    { id: 'cbse', label: 'CBSE Format', labelHi: 'CBSE प्रारूप' },
    { id: 'marks', label: 'Marks Based', labelHi: 'अंक आधारित' },
    { id: 'grade', label: 'Grade/GPA', labelHi: 'ग्रेड/GPA' },
  ];

  return (
    <div className="space-y-4" data-testid="report-card-tab">
      {/* Exam & Format Selection */}
      <div className="bg-white rounded-xl border p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select data-testid="rc-exam-select" value={selectedExam} onChange={(e) => loadClassResults(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white">
            <option value="">{language === 'hi' ? 'परीक्षा चुनें' : 'Select Exam'}</option>
            {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {FORMATS.map(f => (
              <button key={f.id} data-testid={`format-${f.id}`} onClick={() => setFormat(f.id)}
                className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition ${format === f.id ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}>
                {language === 'hi' ? f.labelHi : f.label}
              </button>
            ))}
          </div>
          {selectedStudent && (
            <button onClick={() => { setSelectedStudent(null); setReportCard(null); }}
              className="px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              ← {language === 'hi' ? 'वापस सूची पर' : 'Back to list'}
            </button>
          )}
        </div>
      </div>

      {/* Class Results List */}
      {classResults && !selectedStudent && (
        <div className="bg-white rounded-xl border overflow-hidden">
          {classResults.summary && (
            <div className="p-4 border-b bg-gray-50">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
                <div><div className="text-lg font-bold text-gray-800">{classResults.summary.total_students}</div><div className="text-xs text-gray-500">{language === 'hi' ? 'कुल' : 'Total'}</div></div>
                <div><div className="text-lg font-bold text-green-600">{classResults.summary.passed}</div><div className="text-xs text-gray-500">{language === 'hi' ? 'पास' : 'Passed'}</div></div>
                <div><div className="text-lg font-bold text-red-600">{classResults.summary.failed}</div><div className="text-xs text-gray-500">{language === 'hi' ? 'फेल' : 'Failed'}</div></div>
                <div><div className="text-lg font-bold text-orange-600">{classResults.summary.pass_percentage}%</div><div className="text-xs text-gray-500">{language === 'hi' ? 'पास %' : 'Pass %'}</div></div>
                <div><div className="text-lg font-bold text-blue-600">{classResults.summary.highest_marks}</div><div className="text-xs text-gray-500">{language === 'hi' ? 'उच्चतम' : 'Highest'}</div></div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="class-results-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'रैंक' : 'Rank'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'छात्र' : 'Student'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'अंक' : 'Marks'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">%</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'ग्रेड' : 'Grade'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'परिणाम' : 'Result'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'रिपोर्ट कार्ड' : 'Report Card'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(classResults.students || []).map(s => (
                  <tr key={s.student_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-sm font-bold text-orange-600">#{s.rank}</td>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{s.student_name}</td>
                    <td className="px-4 py-2.5 text-center text-sm">{s.total_marks}/{s.max_marks}</td>
                    <td className="px-4 py-2.5 text-center text-sm font-medium">{s.percentage}%</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        s.grade === 'F' ? 'bg-red-100 text-red-700' :
                        s.grade === 'A+' || s.grade === 'A' ? 'bg-green-100 text-green-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>{s.grade}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`text-xs font-semibold ${s.result === 'PASS' ? 'text-green-600' : 'text-red-600'}`}>{s.result}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button data-testid={`view-rc-${s.student_id}`} onClick={() => loadReportCard(s.student_id)}
                        className="px-3 py-1 bg-orange-50 text-orange-600 rounded text-xs font-medium hover:bg-orange-100 transition">
                        {language === 'hi' ? 'देखें' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Individual Report Card */}
      {reportCard && selectedStudent && (
        <ReportCardView reportCard={reportCard} format={format} language={language} />
      )}

      {loading && <div className="text-center py-8 text-gray-400">Loading...</div>}

      {!selectedExam && (
        <div className="text-center py-16 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">🎓</div>
          <p>{language === 'hi' ? 'परीक्षा चुनें रिपोर्ट कार्ड देखने के लिए' : 'Select an exam to view report cards'}</p>
        </div>
      )}
    </div>
  );
}

// ==================== REPORT CARD VIEW ====================
function ReportCardView({ reportCard, format, language }) {
  const { student, exam, subjects, summary, class_name, section_name } = reportCard;

  return (
    <div className="bg-white rounded-xl border overflow-hidden print:shadow-none" data-testid="report-card-view" id="report-card-print">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 text-center">
        <h2 className="text-xl font-bold tracking-wide">{language === 'hi' ? 'शिक्षा मित्र - रिपोर्ट कार्ड' : 'Shiksha Mitra - Report Card'}</h2>
        <p className="text-sm text-slate-300 mt-1">{exam?.academic_year} | {exam?.name}</p>
      </div>

      {/* Student Info */}
      <div className="p-5 border-b bg-gray-50">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-gray-500">{language === 'hi' ? 'छात्र का नाम' : "Student Name"}</span><p className="font-semibold text-gray-800">{student?.name}</p></div>
          <div><span className="text-gray-500">{language === 'hi' ? 'कक्षा / सेक्शन' : 'Class / Section'}</span><p className="font-semibold text-gray-800">{class_name} {section_name ? `- ${section_name}` : ''}</p></div>
          <div><span className="text-gray-500">{language === 'hi' ? 'प्रवेश संख्या' : 'Admission No.'}</span><p className="font-semibold text-gray-800">{student?.admission_number || '-'}</p></div>
          <div><span className="text-gray-500">{language === 'hi' ? 'पिता का नाम' : "Father's Name"}</span><p className="font-semibold text-gray-800">{student?.father_name || '-'}</p></div>
        </div>
      </div>

      {/* CBSE Format */}
      {format === 'cbse' && (
        <div className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wider">{language === 'hi' ? 'शैक्षणिक प्रदर्शन (Scholastic)' : 'Scholastic Performance'}</h3>
          <table className="w-full border" data-testid="cbse-table">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold border">{language === 'hi' ? 'विषय' : 'Subject'}</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">{language === 'hi' ? 'अधिकतम' : 'Max Marks'}</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">{language === 'hi' ? 'प्राप्तांक' : 'Marks Obtained'}</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">{language === 'hi' ? 'ग्रेड' : 'Grade'}</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">{language === 'hi' ? 'टिप्पणी' : 'Remarks'}</th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((s, i) => (
                <tr key={i} className={s.status === 'fail' ? 'bg-red-50' : ''}>
                  <td className="px-3 py-2 text-sm border font-medium">{s.subject_name}</td>
                  <td className="px-3 py-2 text-sm border text-center">{s.max_marks}</td>
                  <td className="px-3 py-2 text-sm border text-center font-semibold">{s.marks_obtained}</td>
                  <td className="px-3 py-2 text-sm border text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${s.grade === 'F' ? 'bg-red-100 text-red-700' : s.grade === 'A+' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{s.grade}</span>
                  </td>
                  <td className="px-3 py-2 text-xs border text-gray-500">{s.remarks || '-'}</td>
                </tr>
              ))}
              <tr className="bg-orange-50 font-semibold">
                <td className="px-3 py-2 text-sm border">{language === 'hi' ? 'कुल' : 'Total'}</td>
                <td className="px-3 py-2 text-sm border text-center">{summary.total_max}</td>
                <td className="px-3 py-2 text-sm border text-center">{summary.total_marks}</td>
                <td className="px-3 py-2 text-sm border text-center">
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-700">{summary.overall_grade}</span>
                </td>
                <td className="px-3 py-2 text-sm border"></td>
              </tr>
            </tbody>
          </table>

          {/* Co-Scholastic */}
          <h3 className="font-semibold text-gray-800 mb-3 mt-6 text-sm uppercase tracking-wider">{language === 'hi' ? 'सह-शैक्षणिक (Co-Scholastic)' : 'Co-Scholastic Areas'}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: language === 'hi' ? 'कला शिक्षा' : 'Art Education', grade: 'A' },
              { name: language === 'hi' ? 'शारीरिक शिक्षा' : 'Physical Education', grade: 'A' },
              { name: language === 'hi' ? 'अनुशासन' : 'Discipline', grade: 'A' },
              { name: language === 'hi' ? 'कार्य शिक्षा' : 'Work Education', grade: 'B+' },
            ].map((item, i) => (
              <div key={i} className="border rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500">{item.name}</div>
                <div className="text-lg font-bold text-blue-600 mt-1">{item.grade}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Marks Based Format */}
      {format === 'marks' && (
        <div className="p-5">
          <table className="w-full border" data-testid="marks-table-rc">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold border">#</th>
                <th className="px-3 py-2 text-left text-xs font-semibold border">{language === 'hi' ? 'विषय' : 'Subject'}</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">{language === 'hi' ? 'प्राप्तांक' : 'Marks'}</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">{language === 'hi' ? 'अधिकतम' : 'Max'}</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">%</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">{language === 'hi' ? 'परिणाम' : 'Status'}</th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((s, i) => {
                const pct = s.max_marks > 0 ? Math.round(s.marks_obtained / s.max_marks * 100) : 0;
                return (
                  <tr key={i} className={s.status === 'fail' ? 'bg-red-50' : ''}>
                    <td className="px-3 py-2 text-sm border">{i + 1}</td>
                    <td className="px-3 py-2 text-sm border font-medium">{s.subject_name}</td>
                    <td className="px-3 py-2 text-sm border text-center font-bold">{s.marks_obtained}</td>
                    <td className="px-3 py-2 text-sm border text-center">{s.max_marks}</td>
                    <td className="px-3 py-2 text-sm border text-center font-medium">{pct}%</td>
                    <td className="px-3 py-2 text-sm border text-center">
                      <span className={`text-xs font-bold ${s.status === 'pass' ? 'text-green-600' : 'text-red-600'}`}>
                        {s.status === 'pass' ? (language === 'hi' ? 'पास' : 'PASS') : (language === 'hi' ? 'फेल' : 'FAIL')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Grade/GPA Format */}
      {format === 'grade' && (
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
              <div className="text-3xl font-black text-orange-600">{summary.gpa}</div>
              <div className="text-xs text-orange-500 mt-1">GPA (10-point scale)</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <div className="text-3xl font-black text-green-600">{summary.overall_grade}</div>
              <div className="text-xs text-green-500 mt-1">{language === 'hi' ? 'समग्र ग्रेड' : 'Overall Grade'}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
              <div className="text-3xl font-black text-blue-600">#{summary.rank || '-'}</div>
              <div className="text-xs text-blue-500 mt-1">{language === 'hi' ? `कक्षा रैंक (${summary.total_students} में)` : `Class Rank (of ${summary.total_students})`}</div>
            </div>
          </div>
          <table className="w-full border" data-testid="grade-table-rc">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold border">{language === 'hi' ? 'विषय' : 'Subject'}</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">{language === 'hi' ? 'ग्रेड' : 'Grade'}</th>
                <th className="px-3 py-2 text-center text-xs font-semibold border">{language === 'hi' ? 'ग्रेड पॉइंट' : 'Grade Point'}</th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((s, i) => {
                const gp = { 'A+': 10, 'A': 9, 'B+': 8, 'B': 7, 'C': 6, 'D': 5, 'F': 0 }[s.grade] || 0;
                return (
                  <tr key={i} className={s.grade === 'F' ? 'bg-red-50' : ''}>
                    <td className="px-3 py-2 text-sm border font-medium">{s.subject_name}</td>
                    <td className="px-3 py-2 text-sm border text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${s.grade === 'F' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{s.grade}</span>
                    </td>
                    <td className="px-3 py-2 text-sm border text-center font-bold">{gp}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer Summary */}
      <div className="p-5 border-t bg-gray-50">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
          <div><div className="text-sm text-gray-500">{language === 'hi' ? 'कुल अंक' : 'Total Marks'}</div><div className="text-lg font-bold">{summary.total_marks}/{summary.total_max}</div></div>
          <div><div className="text-sm text-gray-500">{language === 'hi' ? 'प्रतिशत' : 'Percentage'}</div><div className="text-lg font-bold text-orange-600">{summary.percentage}%</div></div>
          <div><div className="text-sm text-gray-500">{language === 'hi' ? 'ग्रेड' : 'Grade'}</div><div className="text-lg font-bold">{summary.overall_grade}</div></div>
          <div><div className="text-sm text-gray-500">{language === 'hi' ? 'रैंक' : 'Rank'}</div><div className="text-lg font-bold text-blue-600">#{summary.rank || '-'}/{summary.total_students}</div></div>
          <div>
            <div className="text-sm text-gray-500">{language === 'hi' ? 'परिणाम' : 'Result'}</div>
            <div className={`text-lg font-black ${summary.result === 'PASS' ? 'text-green-600' : 'text-red-600'}`}>{summary.result}</div>
          </div>
        </div>
      </div>

      {/* Share & Print Buttons */}
      <div className="p-4 border-t print:hidden space-y-3">
        {/* WhatsApp Share */}
        <button
          data-testid="share-whatsapp-btn"
          onClick={() => {
            const subjectLines = subjects.map(s => `  ${s.subject_name}: ${s.marks_obtained}/${s.max_marks} (${s.grade})`).join('\n');
            const msg = language === 'hi'
              ? `🎓 *शिक्षा मित्र - रिपोर्ट कार्ड*\n\n👤 छात्र: *${student?.name}*\n📚 कक्षा: ${class_name} ${section_name ? `- ${section_name}` : ''}\n📝 परीक्षा: ${exam?.name} (${exam?.academic_year})\n\n📊 *विषय-वार अंक:*\n${subjectLines}\n\n━━━━━━━━━━━━━━━\n🏆 कुल: *${summary.total_marks}/${summary.total_max}*\n📈 प्रतिशत: *${summary.percentage}%*\n🅰️ ग्रेड: *${summary.overall_grade}*\n🏅 रैंक: *#${summary.rank || '-'}* (${summary.total_students} में)\n✅ परिणाम: *${summary.result === 'PASS' ? 'उत्तीर्ण ✅' : 'अनुत्तीर्ण ❌'}*\n\n_शिक्षा मित्र - AI School Management Platform_`
              : `🎓 *Shiksha Mitra - Report Card*\n\n👤 Student: *${student?.name}*\n📚 Class: ${class_name} ${section_name ? `- ${section_name}` : ''}\n📝 Exam: ${exam?.name} (${exam?.academic_year})\n\n📊 *Subject-wise Marks:*\n${subjectLines}\n\n━━━━━━━━━━━━━━━\n🏆 Total: *${summary.total_marks}/${summary.total_max}*\n📈 Percentage: *${summary.percentage}%*\n🅰️ Grade: *${summary.overall_grade}*\n🏅 Rank: *#${summary.rank || '-'}* (of ${summary.total_students})\n✅ Result: *${summary.result === 'PASS' ? 'PASSED ✅' : 'FAILED ❌'}*\n\n_Shiksha Mitra - AI School Management Platform_`;
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
          }}
          className="w-full py-2.5 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          {language === 'hi' ? 'WhatsApp पर शेयर करें' : 'Share via WhatsApp'}
        </button>

        {/* Copy & Print row */}
        <div className="flex gap-2">
          <button
            data-testid="copy-result-btn"
            onClick={() => {
              const subjectLines = subjects.map(s => `${s.subject_name}: ${s.marks_obtained}/${s.max_marks} (${s.grade})`).join('\n');
              const text = `${student?.name} - ${exam?.name}\n${subjectLines}\nTotal: ${summary.total_marks}/${summary.total_max} | ${summary.percentage}% | Grade: ${summary.overall_grade} | Rank: #${summary.rank || '-'} | ${summary.result}`;
              navigator.clipboard.writeText(text).then(() => {
                const btn = document.querySelector('[data-testid="copy-result-btn"]');
                if (btn) { btn.textContent = language === 'hi' ? '✅ कॉपी हो गया!' : '✅ Copied!'; setTimeout(() => { btn.textContent = language === 'hi' ? '📋 कॉपी करें' : '📋 Copy Result'; }, 2000); }
              });
            }}
            className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition"
          >
            {language === 'hi' ? '📋 कॉपी करें' : '📋 Copy Result'}
          </button>
          <button data-testid="print-report-card" onClick={() => window.print()}
            className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition">
            {language === 'hi' ? '🖨️ प्रिंट करें' : '🖨️ Print'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== SUBJECTS TAB ====================
function SubjectsTab({ token, language }) {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [nameHi, setNameHi] = useState('');
  const [code, setCode] = useState('');
  const [creating, setCreating] = useState(false);

  const loadSubjects = useCallback(async () => {
    const res = await api.get('/api/exams/subjects', token);
    setSubjects(res.subjects || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { loadSubjects(); }, [loadSubjects]);

  const handleCreate = async () => {
    if (!name) return;
    setCreating(true);
    await api.post(`/api/exams/subjects?name=${encodeURIComponent(name)}&name_hindi=${encodeURIComponent(nameHi)}&code=${encodeURIComponent(code)}`, {}, token);
    setCreating(false);
    setName(''); setNameHi(''); setCode('');
    loadSubjects();
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="subjects-tab">
      {/* Add Subject */}
      <div className="bg-white rounded-xl border p-4">
        <h3 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'नया विषय जोड़ें' : 'Add New Subject'}</h3>
        <div className="flex flex-col md:flex-row gap-3">
          <input data-testid="subject-name" placeholder={language === 'hi' ? 'विषय का नाम (English) *' : 'Subject Name *'}
            value={name} onChange={(e) => setName(e.target.value)} className="px-3 py-2 border rounded-lg text-sm flex-1" />
          <input placeholder={language === 'hi' ? 'हिंदी नाम' : 'Hindi Name'}
            value={nameHi} onChange={(e) => setNameHi(e.target.value)} className="px-3 py-2 border rounded-lg text-sm flex-1" />
          <input placeholder={language === 'hi' ? 'कोड (जैसे MAT)' : 'Code (e.g. MAT)'}
            value={code} onChange={(e) => setCode(e.target.value)} className="px-3 py-2 border rounded-lg text-sm w-32" />
          <button data-testid="add-subject-btn" onClick={handleCreate} disabled={creating || !name}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition whitespace-nowrap">
            {creating ? '...' : (language === 'hi' ? '+ जोड़ें' : '+ Add')}
          </button>
        </div>
      </div>

      {/* Subjects List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {subjects.map(s => (
          <div key={s.id} className="bg-white rounded-xl border p-4 hover:shadow-md transition" data-testid={`subject-${s.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 font-bold text-sm">
                {s.code || s.name?.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-gray-800">{s.name}</div>
                {s.name_hindi && <div className="text-xs text-gray-500">{s.name_hindi}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {subjects.length === 0 && (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-4xl mb-2">📚</div>
          <p>{language === 'hi' ? 'कोई विषय नहीं जोड़े गए' : 'No subjects added yet'}</p>
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function ExamsResultsPage({ token }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('exams');
  const [classes, setClasses] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/classes/', token);
      setClasses(res.classes || []);
    })();
  }, [token]);

  const handleSelectExam = (exam) => {
    setSelectedExam(exam);
    setActiveTab('marks');
  };

  const tabs = [
    { id: 'exams', label: language === 'hi' ? 'परीक्षाएं' : 'Exams', icon: '📝' },
    { id: 'marks', label: language === 'hi' ? 'अंक भरें' : 'Marks Entry', icon: '✍️' },
    { id: 'report', label: language === 'hi' ? 'रिपोर्ट कार्ड' : 'Report Cards', icon: '🎓' },
    { id: 'subjects', label: language === 'hi' ? 'विषय' : 'Subjects', icon: '📚' },
  ];

  return (
    <div className="space-y-4" data-testid="exams-page">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          {language === 'hi' ? 'परीक्षा और रिपोर्ट कार्ड' : 'Exams & Report Cards'}
        </h1>
        <p className="text-sm text-gray-500">
          {language === 'hi' ? 'परीक्षा प्रबंधन, अंक प्रविष्टि, और 3 प्रकार के रिपोर्ट कार्ड' : 'Exam management, marks entry & 3 report card formats'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit flex-wrap" data-testid="exams-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === tab.id ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
          >
            <span className="mr-1.5">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'exams' && <ExamsListTab token={token} language={language} classes={classes} onSelectExam={handleSelectExam} />}
      {activeTab === 'marks' && <MarksEntryTab token={token} language={language} classes={classes} selectedExam={selectedExam} />}
      {activeTab === 'report' && <ReportCardTab token={token} language={language} classes={classes} />}
      {activeTab === 'subjects' && <SubjectsTab token={token} language={language} />}
    </div>
  );
}
